// Global Instructions Rule Applied!

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchRunFundChanges } from '../services/investorStatementsService.ts'
import {
  InvestorStatementRun,
  RunFundState,
  StatementStatusCode
} from '../../../state_manager/data_models/investor_statement_run.ts'

/** What is known about one fund of a run. */
export interface RunFundChangeState {
  /** checking = asked, not answered yet; unavailable = the fund's statement
   *  data cannot be read now; error = the request failed. */
  status: 'checking' | 'done' | 'unavailable' | 'error'
  /** why, for unavailable / error */
  message?: string
  /** statement id -> the parts of it that differ from the live read */
  stale: Record<string, string[]>
  staleCount: number
  /** investors of the live read that have no statement */
  missingCount: number
}

export type RunFundChangesMap = Record<string, RunFundChangeState>

const CHECKING: RunFundChangeState = { status: 'checking', stale: {}, staleCount: 0, missingCount: 0 }

/**
 * useRunFundChanges — "out of date" for the funds of a statement run.
 *
 * A stored statement is out of date when the books or the settings it was
 * printed from have changed since. Saying so costs a whole statement read of
 * the fund (a second or two), so it is NOT part of the run read: this hook
 * asks per fund once the run has loaded and nothing is being generated, one
 * fund after another, and each fund reads `checking` until its answer
 * arrives.
 *
 * A fund is asked again when its stored statements change (a generation
 * replaced them) and when `recheck` is called (the user came back to the tab
 * after changing the books).
 */
export function useRunFundChanges(run: InvestorStatementRun | null) {
  const [changes, setChanges] = useState<RunFundChangesMap>({})
  const [round, setRound] = useState(0)
  // fund id -> the signature its answer (or its pending request) is for
  const asked = useRef<Record<string, string>>({})

  // per fund: which stored statements it holds now. Funds being generated,
  // and funds with nothing stored, are not asked.
  const targets = useMemo(() => {
    if (!run || run.prog__is_active) return []
    return run.funds
      .filter(
        (f) =>
          f.prog__counts.generated > 0 &&
          f.prog__state !== RunFundState.queued &&
          f.prog__state !== RunFundState.generating
      )
      .map((f) => {
        const ids = run.statements
          .filter(
            (s) =>
              s.investor_statement_run_funds__id === f.investor_statement_run_funds__id &&
              s.investor_statement_statuses__code === StatementStatusCode.generated
          )
          .map((s) => s.investor_statements__id)
          .sort()
        return { id: f.investor_statement_run_funds__id, signature: `${round}:${ids.join(',')}` }
      })
  }, [run, round])

  useEffect(() => {
    const pending = targets.filter((t) => asked.current[t.id] !== t.signature)
    if (pending.length === 0) return
    pending.forEach((t) => {
      asked.current[t.id] = t.signature
    })
    setChanges((prev) => {
      const next = { ...prev }
      pending.forEach((t) => {
        next[t.id] = CHECKING
      })
      return next
    })
    void (async () => {
      for (const target of pending) {
        const res = await fetchRunFundChanges(target.id, getAccessToken(store.getState().auth))
        // a later generation or recheck owns this fund's answer now. (No
        // cancel-on-cleanup: the effect's cleanup also runs when the run is
        // merely re-read, and the request it would cancel is still the one
        // whose answer is wanted - `asked` alone decides.)
        if (asked.current[target.id] !== target.signature) continue
        const data = res.data
        const state: RunFundChangeState = !data
          ? { ...CHECKING, status: 'error', message: res.error || 'The statements could not be compared with the books' }
          : data.status === 'unavailable'
            ? { ...CHECKING, status: 'unavailable', message: data.status_message ?? undefined }
            : {
                status: 'done',
                stale: Object.fromEntries(
                  data.statements
                    .filter((s) => s.prog__is_stale)
                    .map((s) => [s.investor_statements__id, s.prog__changed_parts])
                ),
                staleCount: data.prog__stale_count,
                missingCount: data.prog__missing_count
              }
        setChanges((prev) => ({ ...prev, [target.id]: state }))
      }
    })()
  }, [targets])

  /** Ask every fund again (the books may have changed elsewhere). */
  const recheck = useCallback(() => setRound((r) => r + 1), [])

  return { changes, recheck }
}
